import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { Lock, Loader2, CheckCircle, ArrowLeft, AlertTriangle } from 'lucide-react';
import NoDoublesLogo from '@/components/marketing/NoDoublesLogo';
import { PasswordStrengthMeter } from '@/components/auth/PasswordStrengthMeter';
import { calculatePasswordStrength } from '@/lib/auth/passwordStrength';

const schema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Must contain an uppercase letter')
      .regex(/[a-z]/, 'Must contain a lowercase letter')
      .regex(/\d/, 'Must contain a number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof schema>;

export default function ResetPassword() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [hasValidSession, setHasValidSession] = useState<boolean | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  useEffect(() => {
    supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setHasValidSession(true);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setHasValidSession(!!session);
    });
  }, []);

  const handleSubmit = async (values: FormValues) => {
    const strength = calculatePasswordStrength(values.password);
    if (!strength.allRequirementsMet) {
      toast({
        title: 'Password Requirements',
        description: 'Please meet all password requirements.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    const { error } = await supabase.auth.updateUser({ password: values.password });

    if (error) {
      toast({
        title: 'Reset Failed',
        description: 'Unable to reset your password. The link may have expired. Please request a new one.',
        variant: 'destructive',
      });
    } else {
      setResetComplete(true);
      setTimeout(() => navigate('/home'), 3000);
    }
    setIsLoading(false);
  };

  const watchedPassword = form.watch('password');

  if (hasValidSession === null) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-surface to-muted flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-surface to-muted flex flex-col justify-start md:justify-center px-4 py-6 md:py-12">
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <NoDoublesLogo size={80} className="mx-auto mb-4" />
        </div>

        <div className="bg-card rounded-2xl shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-sun-gold to-fairway-light" />

          <div className="p-8 md:p-10">
            {!hasValidSession ? (
              <div className="text-center space-y-4">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                  <AlertTriangle className="h-8 w-8 text-destructive" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Invalid or Expired Link</h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  This password reset link is invalid or has expired. Please request a new one.
                </p>
                <div className="pt-4">
                  <Link to="/forgot-password">
                    <Button className="w-full h-12 bg-gradient-primary text-primary-foreground">
                      Request New Link
                    </Button>
                  </Link>
                </div>
              </div>
            ) : resetComplete ? (
              <div className="text-center space-y-4">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <CheckCircle className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Password Reset!</h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Your password has been successfully reset. Redirecting you now...
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">Set a new password</h2>
                  <p className="text-muted-foreground text-sm">
                    Choose a strong password to protect your account.
                  </p>
                </div>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-foreground">
                            New Password
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none z-10" />
                              <Input
                                {...field}
                                type="password"
                                placeholder="Enter your new password"
                                className="pl-10 h-12 border-border focus:border-primary focus:ring-primary"
                                disabled={isLoading}
                                autoFocus
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                          <PasswordStrengthMeter password={watchedPassword} />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-foreground">
                            Confirm Password
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none z-10" />
                              <Input
                                {...field}
                                type="password"
                                placeholder="Confirm your new password"
                                className="pl-10 h-12 border-border focus:border-primary focus:ring-primary"
                                disabled={isLoading}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-12 bg-gradient-primary hover:shadow-green transition-all duration-200 text-primary-foreground"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        'Reset Password'
                      )}
                    </Button>
                  </form>
                </Form>

                <div className="text-center pt-4 border-t border-border">
                  <Link
                    to="/auth"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center"
                  >
                    <ArrowLeft className="mr-1 h-4 w-4" />
                    Back to sign in
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
